import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, Download, Workflow } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Permission, useCan } from "@/components/session-provider";
import { RelativeTime } from "@/components/RelativeTime";
import {
  AUTOMATION_REPORT_DAYS,
  automationReportExportUrl,
  getAutomationReport,
  type AutomationReport,
  type AutomationReportQuery,
} from "@/lib/reports-automation";
import type { AutomationRunMode } from "@/lib/automation";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/reports/automation")({
  component: AutomationActivityPage,
});

/**
 * **Automation activity.**
 *
 * ⚠️ **THE LIVE / WHAT-IF SPLIT IS THE REPORT.** "These rules would have applied 143 times" is the
 * figure somebody reads before putting a rule live, and it is the only figure that cannot be
 * obtained any other way — once a rule has acted, what it would have done can no longer be asked.
 * Every total here is drawn as two, never one.
 *
 * ⚠️ **Gated on `automation.manage`, not on a reporting tier**, and the server enforces it. The
 * payload is about rules rather than about requests or colleagues, which is the whole reason this
 * report can exist without a clearance question (D-165).
 *
 * ⚠️ **It carries no request and no person.** A run row names a ticket; this report never projects
 * it, because resolving it would make a reader's own ticket reach decide what the aggregates say —
 * two administrators reading different totals for the same rule, on a report about configuration.
 * The rule's own run history is where a run names its request.
 *
 * ⚠️ **No drill-through of its own.** Every figure links into the rule's existing run history on
 * `/automation`, behind the same permission — D-144's decision applied a second time, because a
 * second reader would be a second definition of "read the run history".
 */
function AutomationActivityPage() {
  const allowed = useCan(Permission.AutomationManage);

  const [days, setDays] = useState<number>(7);
  const [mode, setMode] = useState<AutomationRunMode | "">("");
  const [report, setReport] = useState<AutomationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const query = useMemo<AutomationReportQuery>(
    () => ({ days, mode: mode === "" ? null : mode }),
    [days, mode],
  );

  useEffect(() => {
    if (!allowed) return;

    let current = true;
    setLoading(true);
    setError(null);

    getAutomationReport(query)
      .then((r) => {
        if (current) setReport(r);
      })
      .catch((e) => {
        // ⚠️ A failure renders as a failure, never as an empty report (D-122). "No rules have
        // done anything" and "this did not load" are different facts, and the first is a claim
        // about somebody's own configuration.
        if (current) {
          setReport(null);
          setError(e instanceof ApiError ? e.message : "That report could not be loaded.");
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [allowed, query]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">This report is not available to you.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Reports
      </Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Workflow className="h-5 w-5" /> Automation Activity
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            What the standing rules did, what they would have done, and what refused them.
          </p>
        </div>

        <a
          href={automationReportExportUrl(query)}
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-border px-4 text-sm font-medium transition hover:bg-muted"
        >
          <Download className="h-4 w-4" /> Export
        </a>
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="text-xs text-muted-foreground" htmlFor="period">
          Period
        </label>
        <select
          id="period"
          className="h-9 rounded-md border border-input bg-background px-2 text-base md:text-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {AUTOMATION_REPORT_DAYS.map((d) => (
            <option key={d} value={d}>
              Last {d} {d === 1 ? "day" : "days"}
            </option>
          ))}
        </select>

        <label className="ml-2 text-xs text-muted-foreground" htmlFor="mode">
          Mode
        </label>
        <select
          id="mode"
          className="h-9 rounded-md border border-input bg-background px-2 text-base md:text-sm"
          value={mode}
          onChange={(e) => setMode(e.target.value as AutomationRunMode | "")}
        >
          <option value="">Live and what-if</option>
          <option value="Live">Live only</option>
          <option value="WhatIf">What-if only</option>
        </select>
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !report && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}

      {report && (
        <>
          {/*
            ⚠️ THE COVERAGE NOTICE COMES FIRST, before any figure. Run history is pruned, so a
            number here is bounded by something the reader cannot see — and a quiet rule looks
            like a well-behaved one, which is the direction nobody checks (D-53).
          */}
          <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            Run history is kept for {report.coverage.retentionDays} days, so no longer period can be
            answered.{" "}
            {report.coverage.earliestRetainedRunUtc ? (
              <>
                The oldest run still kept is from{" "}
                <RelativeTime value={report.coverage.earliestRetainedRunUtc} />.
              </>
            ) : (
              <>Nothing has run yet.</>
            )}{" "}
            Days are counted in UTC.
          </p>

          <section className="mt-4 grid gap-3 sm:grid-cols-2">
            <Tile
              tone="live"
              heading="Carried out"
              figure={report.totals.live.applied}
              note={`${report.totals.live.total.toLocaleString()} live runs in the period`}
            />
            <Tile
              tone="whatif"
              heading="Would have been carried out"
              figure={report.totals.whatIf.applied}
              note={`${report.totals.whatIf.total.toLocaleString()} what-if runs — nothing was changed`}
            />
          </section>

          <Panel
            help="auto-outcomes"
            title="Outcomes"
            note={`${report.totals.total.toLocaleString()} runs in total`}
          >
            <Table
              head={["Outcome", "Live", "What-if", "Total"]}
              rows={report.outcomes.map((o) => [o.outcome, o.live, o.whatIf, o.total])}
            />
          </Panel>

          <Panel
            help="auto-rule"
            title="By rule"
            note="Applied is what happened; would have applied is what a rule in what-if reported."
          >
            {report.rules.length === 0 ? (
              <Empty>No rule has run in this period.</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[52rem] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3 font-medium">Rule</th>
                      <th className="py-2 pr-3 font-medium">Applied</th>
                      <th className="py-2 pr-3 font-medium">Would have</th>
                      <th className="py-2 pr-3 font-medium">Did not match</th>
                      <th className="py-2 pr-3 font-medium">Refused</th>
                      <th className="py-2 pr-3 font-medium">Failed</th>
                      <th className="py-2 pr-3 font-medium">Last run</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rules.map((rule) => (
                      <tr key={rule.ruleId} className="border-t border-border align-top">
                        <td className="py-2 pr-3">
                          {/*
                            ⚠️ Links to the rule's own run history rather than to a drill-through
                            of this report's own — one definition of "read the run history".
                          */}
                          <Link
                            to="/automation"
                            className="font-medium text-foreground hover:underline"
                          >
                            {rule.name}
                          </Link>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                            <span>{rule.trigger}</span>
                            {!rule.isEnabled && <Badge tone="muted">Off</Badge>}
                            {rule.isEnabled && rule.modeNow === "WhatIf" && (
                              <Badge tone="whatif">What-if now</Badge>
                            )}
                            {rule.suspendedReason && (
                              <Badge tone="bad">{rule.suspendedReason}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3 tabular-nums">{rule.applied.toLocaleString()}</td>
                        <td className="py-2 pr-3 tabular-nums text-sky-700 dark:text-sky-300">
                          {rule.wouldApply.toLocaleString()}
                        </td>
                        <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                          {rule.notMatched.toLocaleString()}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">{rule.refused.toLocaleString()}</td>
                        <td className="py-2 pr-3 tabular-nums">{rule.failed.toLocaleString()}</td>
                        <td className="py-2 pr-3 text-muted-foreground">
                          {rule.lastRunAtUtc ? <RelativeTime value={rule.lastRunAtUtc} /> : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {/*
            ⚠️ THE ACTIONABLE HALF, and the reason this report is worth opening. "Refused 47
            times" says a rule is wrong; the reason says what to change.
          */}
          <Panel
            help="auto-refused"
            title="Why actions were refused"
            note="The server's own reason, as recorded against the step that was refused."
          >
            {report.refusals.length === 0 ? (
              <Empty>Nothing was refused in this period.</Empty>
            ) : (
              <Table
                head={["Action", "Outcome", "Reason", "Times"]}
                rows={report.refusals.map((r) => [r.kind, r.outcome, r.reason ?? "—", r.count])}
              />
            )}
          </Panel>

          <Panel
            help="auto-action"
            title="By action"
            note="Which action in a rule is the one being refused."
          >
            {report.actions.length === 0 ? (
              <Empty>No action has run in this period.</Empty>
            ) : (
              <Table
                head={["Action", "Applied", "No change", "Refused", "Failed", "Not attempted"]}
                rows={report.actions.map((a) => [
                  a.kind,
                  a.applied,
                  a.noChange,
                  a.refused,
                  a.failed,
                  a.notAttempted,
                ])}
              />
            )}
          </Panel>

          <Panel help="auto-day" title="By day" note="Days are UTC, not your own timezone.">
            {report.days.length === 0 ? (
              <Empty>Nothing has run in this period.</Empty>
            ) : (
              <Table
                head={["Day (UTC)", "Applied", "Would have", "Other"]}
                rows={report.days.map((d) => [
                  d.dayUtc.slice(0, 10),
                  d.applied,
                  d.wouldApply,
                  d.other,
                ])}
              />
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

function Tile({
  tone,
  heading,
  figure,
  note,
}: {
  tone: "live" | "whatif";
  heading: string;
  figure: number;
  note: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border p-4",
        tone === "whatif"
          ? "border-sky-500/30 bg-sky-500/5"
          : "border-emerald-500/30 bg-emerald-500/5",
      )}
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{heading}</p>
      <p
        className={cn(
          "mt-1 text-3xl font-semibold tabular-nums",
          tone === "whatif"
            ? "text-sky-700 dark:text-sky-300"
            : "text-emerald-700 dark:text-emerald-300",
        )}
      >
        {figure.toLocaleString()}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{note}</p>
    </div>
  );
}

function Panel({
  title,
  note,
  help,
  children,
}: {
  /** Key into the help registry (`lib/help.ts`). Draws the ⓘ beside the title. */
  help?: string;
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="group/card mt-4 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {help && <InfoTip id={help} />}
      </div>
      {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            {head.map((h) => (
              <th key={h} className="py-2 pr-3 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              {row.map((cell, j) => (
                <td key={j} className={cn("py-2 pr-3", typeof cell === "number" && "tabular-nums")}>
                  {typeof cell === "number" ? cell.toLocaleString() : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: "muted" | "whatif" | "bad";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium",
        tone === "bad"
          ? "bg-destructive/15 text-destructive"
          : tone === "whatif"
            ? "bg-sky-500/15 text-sky-700 dark:text-sky-300"
            : "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}
